import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, fmtDate, DEBT_CATEGORIES, catColor } from "@/lib/constants";
import { Target, Check, CheckCircle2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

export default function MonthlyPayments({ debts, plan, onPaid, onPaidOff }) {
  const [amounts, setAmounts] = useState({});
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    setAmounts(Object.fromEntries(debts.map(d => [d.id, d.plan.recommended])));
  }, [debts]);

  const pay = async (d) => {
    const amount = parseFloat(amounts[d.id]);
    if (!amount || amount <= 0) { toast.error("Enter a positive amount"); return; }
    setPaying(d.id);
    try {
      const { data } = await api.post(`/bills/${d.bill_id}/pay`, { amount });
      const dp = data.debt_payment;
      if (dp?.paid_off) onPaidOff?.(dp);
      else toast.success(`Paid ${fmt(dp.payment)} on ${d.name}`, {
        description: `${fmt(dp.interest)} interest · ${fmt(dp.principal)} principal · balance now ${fmt(dp.balance_after)}`,
      });
      onPaid?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    finally { setPaying(null); }
  };

  const active = debts.filter(d => !d.paid_off && !d.idle && !d.paid_this_month);
  const paidThisMonth = debts.filter(d => d.paid_this_month && !d.paid_off);
  const total = active.reduce((s, d) => s + (parseFloat(amounts[d.id]) || 0), 0);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="monthly-payments">
      <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold text-lg flex items-center gap-2">This month's payments <HelpTip k="target_debt" /> <HelpTip k="freed_minimums" /></div>
          <div className="text-xs text-muted-foreground" data-testid="monthly-payments-subtitle">
            {{ snowball: "Snowball", avalanche: "Avalanche", cfi: "Cash Flow Index" }[plan.strategy] || "Payoff"} plan · minimums everywhere, {fmt(plan.snowball_amount)} snowball on the target
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total to pay</div>
          <div className="font-mono font-bold text-lg" data-testid="monthly-total">{fmt(total)}</div>
        </div>
      </div>
      {active.length === 0 && paidThisMonth.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Nothing due — no account carries a balance.</div>}
      {active.length === 0 && paidThisMonth.length > 0 && <div className="p-6 text-center text-emerald-500 text-sm font-medium">All caught up — every payment for this month is done. 🎉</div>}
      <ul className="divide-y divide-border/60">
        {active.map(d => (
          <li key={d.id} data-testid={`payment-row-${d.id}`}
            className={`p-4 flex items-center gap-4 flex-wrap ${d.plan.is_target ? "bg-indigo-500/5" : ""}`}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: catColor(DEBT_CATEGORIES, d.category) }} />
            <div className="min-w-[160px] flex-1">
              <div className="font-semibold flex items-center gap-2">
                {d.name}
                {d.plan.is_target && (
                  <span data-testid={`payment-target-${d.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-indigo-500/15 text-indigo-400 border border-indigo-500/30">
                    <Target className="h-2.5 w-2.5" /> Target
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground">Due {fmtDate(d.due_date)} · balance {fmt(d.balance)} · {d.promo_active ? <span className="text-violet-400">{d.effective_rate}% promo APR</span> : `${d.interest_rate}% APR`}</div>
            </div>
            <div className="text-right text-xs font-mono min-w-[140px]">
              <div>Min {fmt(d.plan.minimum)}</div>
              {d.plan.extra > 0 && <div className="text-indigo-400">+ {fmt(d.plan.extra)} snowball</div>}
              {d.plan.utilization_boost > 0 && <div className="text-emerald-500" data-testid={`payment-boost-${d.id}`}>+ {fmt(d.plan.utilization_boost)} to {d.utilization_target?.goal_pct ?? 30}% util</div>}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                <input type="number" min={0} step="0.01" value={amounts[d.id] ?? ""} data-testid={`payment-amount-${d.id}`}
                  onChange={e => setAmounts(a => ({ ...a, [d.id]: e.target.value }))}
                  className="w-28 pl-6 pr-2 py-1.5 rounded-lg bg-background border border-border text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
              </div>
              <button onClick={() => pay(d)} disabled={paying === d.id} data-testid={`payment-pay-${d.id}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition disabled:opacity-60">
                <Check className="h-3.5 w-3.5" /> {paying === d.id ? "…" : "Pay"}
              </button>
            </div>
          </li>
        ))}
        {paidThisMonth.map(d => (
          <li key={d.id} className="p-3 px-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid={`payment-paid-month-${d.id}`}>
            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
            <span className="font-medium text-foreground">{d.name}</span>
            <span className="text-[11px]">paid this month{d.last_paid_at ? ` on ${fmtDate(d.last_paid_at)}` : ""} · balance now {fmt(d.balance)}</span>
          </li>
        ))}
        {debts.filter(d => d.paid_off).map(d => (
          <li key={d.id} className="p-3 px-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid={`payment-row-${d.id}`}>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" /> <span className="line-through">{d.name}</span>
            <span className="text-[11px]">paid off {d.paid_off_at ? fmtDate(d.paid_off_at) : ""} · {fmt(d.minimum_payment)}/mo freed into the snowball</span>
          </li>
        ))}
        {debts.filter(d => d.idle).map(d => (
          <li key={d.id} className="p-3 px-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid={`payment-row-${d.id}`}>
            <CreditCard className="h-4 w-4 text-emerald-500" /> <span>{d.name}</span>
            <span className="text-[11px]">no balance · nothing due · {fmt(d.credit_limit || 0)} limit counted in utilization</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
